import * as React from "react"
import { ChevronDown, Rss, Search } from "lucide-react"

import { HELP_TEXT, META_TEXT, TAP_TARGET } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import { Badge } from "@/shadcn/components/ui/badge"
import { Button } from "@/shadcn/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu"
import { Input } from "@/shadcn/components/ui/input"
import { Separator } from "@/shadcn/components/ui/separator"
import { Tabs, TabsList, TabsTrigger } from "@/shadcn/components/ui/tabs"
import { Monogram } from "./marks"
import { type FeedResult } from "./types"

/**
 * Step 1 · find a feed.
 *
 * The search field, language picker, suggested-feed categories and the result
 * rows. Every control is inert until handed its callback, so the design harness
 * and a half-wired flow both render.
 */

/**
 * Feed-search languages, in prod's order (selected language first, then the
 * rest as prod lists them). Rendered in each language's own name, so they stay
 * as-is rather than being localised.
 */
export const SEARCH_LANGUAGES = [
  "English",
  "Español",
  "Français",
  "Italiano",
  "日本語",
  "Português",
  "Deutsch",
  "Nederlands",
  "Русский",
  "中文",
]

/**
 * Short codes for the compact language button shown below `sm`, where the full
 * name can't sit beside the search field. Latin ISO-style codes rather than
 * clipped native names, so every code occupies the same width in the 44px box;
 * a language outside this map falls back to its first two characters.
 */
const LANGUAGE_CODE: Record<string, string> = {
  English: "EN",
  Español: "ES",
  Français: "FR",
  Italiano: "IT",
  日本語: "JA",
  Português: "PT",
  Deutsch: "DE",
  Nederlands: "NL",
  Русский: "RU",
  中文: "ZH",
}

function languageCode(language: string) {
  return LANGUAGE_CODE[language] ?? language.slice(0, 2).toUpperCase()
}

/**
 * The feed's own icon where it has one, its monogram otherwise.
 *
 * A feed icon is whatever the publisher serves, so a dead or hotlink-blocked URL
 * is routine rather than exceptional — falling back on error keeps the row
 * identifiable instead of showing a broken-image glyph.
 */
function FeedIcon({ result }: { result: FeedResult }) {
  const [failed, setFailed] = React.useState(false)

  if (!result.iconUrl || failed) {
    return (
      <Monogram
        letter={result.monogram}
        color={result.monogramColor}
        className="tw:size-11"
      />
    )
  }

  return (
    <img
      src={result.iconUrl}
      alt=""
      onError={() => setFailed(true)}
      className="tw:size-11 tw:shrink-0 tw:rounded-md tw:bg-muted tw:object-contain"
    />
  )
}

/**
 * Mobile: Connect drops to its own full-width row. Keeping the desktop
 * three-column layout at 303px left ~62px for the feed name once the velocity
 * badge took its share, which truncated every name in the mock data. Stacking
 * returns the text column to ~243px, and the description line-clamps to two
 * lines instead of being cut at ~22 characters.
 *
 * At `sm` and up the inner wrapper restores the original single row: the same
 * 16px gaps, the same `min-w-0 flex-1` truncation behaviour.
 */
function FeedResultRow({
  result,
  onPreview,
  onConnect,
}: {
  result: FeedResult
  onPreview?: (result: FeedResult) => void
  onConnect?: (result: FeedResult) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:py-4 tw:sm:flex-row tw:sm:items-center tw:sm:gap-4">
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-4">
        <FeedIcon result={result} />
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-[3px]">
          {/* While the row is stacked (below `sm`) the text column spans the full
              width, so the pill is pushed to the right edge; once the row goes
              horizontal it packs next to the name again, as drawn. */}
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:sm:justify-start">
            <span className="tw:truncate tw:text-base tw:font-medium tw:text-foreground">
              {result.name}
            </span>
            {result.velocity ? (
              <Badge variant="secondary" className="tw:shrink-0">
                {result.velocity}
              </Badge>
            ) : null}
          </div>
          {/* line-clamp both ways rather than clamp→truncate: mixing them means
              `sm:line-clamp-none`'s `overflow: visible` and `sm:truncate`'s
              `overflow: hidden` both target the same breakpoint, and only
              Tailwind's internal rule order decides the winner. `line-clamp-1`
              is visually identical to truncate for one line, with no conflict. */}
          <p className="tw:line-clamp-2 tw:text-sm tw:text-muted-foreground tw:sm:line-clamp-1">
            {result.description}
          </p>
          {/* A button, not a span: at mobile this needs a real hit box rather
              than a 20px line of text. */}
          <button
            type="button"
            className="tw:w-fit tw:text-sm tw:font-medium tw:text-primary tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:py-2 tw:md:py-0"
            onClick={onPreview ? () => onPreview(result) : undefined}
            aria-label={`${result.linkLabel ?? "Preview"} ${result.name}`}
          >
            {result.linkLabel ?? "Preview"}
          </button>
        </div>
      </div>
      <Button
        variant="outline"
        className={cn(TAP_TARGET, "tw:w-full tw:shrink-0 tw:sm:w-auto")}
        onClick={onConnect ? () => onConnect(result) : undefined}
        // every row's button reads the same, so the name says which feed it connects
        aria-label={`${result.connectLabel ?? "Connect"} ${result.name}`}
      >
        {result.connectLabel ?? "Connect"}
      </Button>
    </div>
  )
}

export function FindStep({
  findTitle,
  query,
  language,
  languages,
  categories,
  activeCategory,
  resultsCaption,
  results,
  loading,
  loadingLabel,
  searching,
  validating,
  onQueryChange,
  onLanguageChange,
  onCategoryChange,
  onPreview,
  onConnect,
}: {
  findTitle: string
  query?: string
  language: string
  languages: string[]
  categories: string[]
  activeCategory: string
  resultsCaption?: string
  results: FeedResult[]
  /**
   * Results are on their way — the catalogue on arrival, or a search including
   * the debounce before its request is even sent. Draws skeleton rows, which is
   * what keeps an in-flight search from reading as a failed one.
   */
  loading?: boolean
  /** Names the wait for assistive tech. Defaults to "Loading feeds". */
  loadingLabel?: string
  /**
   * A search is what produced (or will produce) `results`, rather than the
   * suggested-feed catalogue. Hides the category strip and is the only state in
   * which an empty result set says "No feeds found".
   */
  searching?: boolean
  /**
   * The feed the user has just picked is being validated. Takes the whole
   * suggested-feed block away — separator, heading, category strip and caption
   * — rather than leaving a catalogue the user is finished with sitting behind
   * the wait. `loading` still draws the skeleton rows, so the wait itself is
   * still reported.
   */
  validating?: boolean
  /**
   * Makes the search field live. Without it the field is read-only, since a
   * value-bearing input with no handler would swallow every keystroke.
   */
  onQueryChange?: (query: string) => void
  onLanguageChange?: (language: string) => void
  onCategoryChange?: (category: string) => void
  onPreview?: (result: FeedResult) => void
  onConnect?: (result: FeedResult) => void
}) {
  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-6 tw:md:gap-8">
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
        <div className="tw:flex tw:items-center tw:gap-3">
          <Rss
            className="tw:size-[26px] tw:text-muted-foreground"
            strokeWidth={1.5}
          />
          <p className="tw:text-base tw:font-medium tw:text-foreground">
            {findTitle}
          </p>
        </div>

        {/* One row at every width. Stacking below `sm` gave the language a
            full-width 44px row of its own — 56px spent on a setting almost
            nobody changes, sitting directly under the primary action. */}
        <div className="tw:flex tw:w-full tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-4 tw:shadow-sm">
          <div className="tw:relative tw:flex-1">
            <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2.5 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
            <Input
              readOnly={!onQueryChange}
              value={onQueryChange ? (query ?? "") : undefined}
              defaultValue={onQueryChange ? undefined : query}
              onChange={
                onQueryChange
                  ? (event) => onQueryChange(event.target.value)
                  : undefined
              }
              placeholder="Search RSS Feeds"
              className={cn(TAP_TARGET, "tw:pl-8")}
            />
          </div>
          {/* modal={false}: a modal Radix menu locks the page scroll while
              it is open, and this sits above a list the user is reading. */}
          <DropdownMenu modal={false}>
            {/* Below `sm` this is a 44px square showing the language code, so the
                card stays one row and the field keeps 215px of the 271px card.
                At `sm` and up it returns to the full name + chevron as drawn. */}
            <DropdownMenuTrigger
              className="tw:inline-flex tw:size-11 tw:shrink-0 tw:items-center tw:justify-center tw:gap-2 tw:rounded-lg tw:border tw:border-input tw:text-sm tw:font-medium tw:text-muted-foreground tw:transition-colors tw:outline-none tw:hover:bg-muted tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:data-[state=open]:bg-muted tw:sm:size-auto tw:sm:justify-start tw:sm:py-2.5 tw:sm:pr-3 tw:sm:pl-3.5 tw:sm:font-normal"
              aria-label={`Search language: ${language}`}
            >
              <span className="tw:sm:hidden">{languageCode(language)}</span>
              <span className="tw:hidden tw:sm:inline">{language}</span>
              <ChevronDown className="tw:hidden tw:size-4 tw:sm:block" />
            </DropdownMenuTrigger>
            {/* Portal patch: content renders outside `.tw-root`, so it carries
                the class itself to keep the scoped preflight. */}
            <DropdownMenuContent align="end" className="tw-root tw:w-44">
              <DropdownMenuRadioGroup
                value={language}
                onValueChange={onLanguageChange}
              >
                {languages.map((option) => (
                  <DropdownMenuRadioItem key={option} value={option}>
                    {option}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* The placeholder is an attribute, so it can't vary by breakpoint
            without client-side JS — "Search RSS Feeds" therefore applies at
            every width, and no longer advertises that the field also takes a
            pasted URL. This line carries that path instead; until now it was
            only mentioned in the "No feeds found" empty state, i.e. after a
            search had already failed. */}
        <p className={HELP_TEXT}>Or paste a feed URL to add it directly.</p>
      </div>

      {categories.length && !searching && !validating ? (
        <>
          <Separator />
          <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
            <p className="tw:text-base tw:font-medium tw:text-foreground">
              Suggested feeds
            </p>
            {/* TabsList is `inline-flex w-fit` and can't shrink: six categories
                are ~390px of min-content, so below ~400px the strip would push
                the page sideways. Scroll it instead. `-my-1 py-1` keeps room for
                the triggers' focus ring, which the scroll container would clip
                (overflow-x auto forces overflow-y to auto too). */}
            <div className="tw:-my-1 tw:w-full tw:overflow-x-auto tw:py-1">
              {/* w-max, not w-fit: `fit-content` is clamped to the available
                  width, so inside the 303px scroll port the strip capped at
                  303px, its triggers squeezed past the bg-muted pill, and the
                  labels clipped mid-word. `max-content` lets it take its natural
                  ~392px so the pill covers every tab and it scrolls cleanly. */}
              <Tabs
                value={activeCategory}
                onValueChange={onCategoryChange}
                className="tw:w-max"
              >
                <TabsList className="tw:w-max">
                  {categories.map((category) => (
                    <TabsTrigger key={category} value={category}>
                      {category}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            {resultsCaption && !loading ? (
              <p className={cn("tw:font-medium", META_TEXT)}>
                {resultsCaption}
              </p>
            ) : null}
            <ResultsList
              results={results}
              loading={loading}
              loadingLabel={loadingLabel}
              searching={searching}
              onPreview={onPreview}
              onConnect={onConnect}
            />
          </div>
        </>
      ) : (
        <ResultsList
          results={results}
          loading={loading}
          loadingLabel={loadingLabel}
          searching={searching}
          onPreview={onPreview}
          onConnect={onConnect}
        />
      )}
    </div>
  )
}

/**
 * Placeholder row, matching FeedResultRow's own metrics: a size-11 icon, two
 * text lines and the Connect button's footprint. Same shape so the list doesn't
 * resize when the real rows land.
 */
function ResultRowSkeleton() {
  return (
    <div
      aria-hidden
      className="tw:flex tw:flex-col tw:gap-3 tw:py-4 tw:sm:flex-row tw:sm:items-center tw:sm:gap-4"
    >
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-4">
        <div className="tw:size-11 tw:shrink-0 tw:animate-pulse tw:rounded-md tw:bg-muted" />
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2">
          <div className="tw:h-4 tw:w-1/3 tw:animate-pulse tw:rounded-xs tw:bg-muted" />
          <div className="tw:h-3 tw:w-3/4 tw:animate-pulse tw:rounded-xs tw:bg-muted" />
        </div>
      </div>
      <div className="tw:h-9 tw:w-full tw:shrink-0 tw:animate-pulse tw:rounded-md tw:bg-muted tw:sm:w-24" />
    </div>
  )
}

/**
 * The rows, or what stands in for them.
 *
 * Three empty cases, and only one of them is "no feeds found":
 *
 *   · loading — skeleton rows. Covers every wait the caller can be in: the
 *     suggested-feed catalogue on arrival, a search still in flight (including
 *     its debounce, which is the caller's to report), and the feed the user has
 *     just PICKED being validated — the step stays mounted for that round trip,
 *     so without it the cleared results read as a failed search.
 *   · a completed SEARCH with no matches — the "No feeds found" panel, whose
 *     copy tells the user what to try next. This is the only case it fits.
 *   · anything else empty — nothing at all. A category with no feeds and a
 *     catalogue that failed to load are not failed searches, and telling
 *     someone who has searched for nothing that nothing was found is a
 *     non-sequitur.
 */
function ResultsList({
  results,
  loading,
  loadingLabel = "Loading feeds",
  searching,
  onPreview,
  onConnect,
}: {
  results: FeedResult[]
  loading?: boolean
  loadingLabel?: string
  searching?: boolean
  onPreview?: (result: FeedResult) => void
  onConnect?: (result: FeedResult) => void
}) {
  if (loading) {
    return (
      <div className="tw:flex tw:w-full tw:flex-col" role="status" aria-busy>
        <span className="tw:sr-only">{loadingLabel}</span>
        {[0, 1, 2].map((i) => (
          <div key={i} className="tw:contents">
            {i > 0 ? <Separator /> : null}
            <ResultRowSkeleton />
          </div>
        ))}
      </div>
    )
  }

  if (!results.length) {
    if (!searching) return null

    return (
      <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-dashed tw:border-border tw:px-6 tw:py-8 tw:text-center tw:md:py-12">
        <p className="tw:text-sm tw:font-medium tw:text-foreground">
          No feeds found
        </p>
        <p className="tw:text-sm tw:text-muted-foreground">
          Try another topic, or paste an RSS URL above.
        </p>
      </div>
    )
  }

  /*
    `id` is the feed URL, and nothing upstream makes it unique: the catalogue
    can hold the same URL twice in a category. React can't reconcile duplicate
    keys — on a category switch it removes one copy and orphans the other, which
    then sits atop every later list and gains a twin each cycle. Suffixing
    repeats keeps the keys stable while making them distinct.
  */
  const seen = new Map<string, number>()
  const keys = results.map((result) => {
    const count = (seen.get(result.id) ?? 0) + 1
    seen.set(result.id, count)
    return count > 1 ? `${result.id}#${count}` : result.id
  })

  return (
    <div className="tw:flex tw:w-full tw:flex-col">
      {results.map((result, i) => (
        <div key={keys[i]} className="tw:contents">
          {i > 0 ? <Separator /> : null}
          <FeedResultRow
            result={result}
            onPreview={onPreview}
            onConnect={onConnect}
          />
        </div>
      ))}
    </div>
  )
}
